import {
  getPlatformStorageItem,
  removePlatformStorageItem,
  setPlatformStorageItem,
} from "@/data/storage/platform_storage";

interface ErrorLogEntry {
  timestamp: number;
  message: string;
  stack?: string;
  source?: string;
}

class ErrorLogger {
  private static readonly STORAGE_KEY = "app_runtime_errors";
  private static readonly MAX_ERRORS = 100;

  static logError(error: Error | string, source?: string) {
    try {
      const errorEntry: ErrorLogEntry = {
        timestamp: Date.now(),
        message: typeof error === "string" ? error : error.message,
        stack: typeof error === "object" ? error.stack : undefined,
        source,
      };

      const existingErrors = this.getStoredErrors();
      const updatedErrors = [errorEntry, ...existingErrors];

      // Keep only the latest MAX_ERRORS entries
      const trimmedErrors = updatedErrors.slice(0, this.MAX_ERRORS);

      setPlatformStorageItem(this.STORAGE_KEY, JSON.stringify(trimmedErrors));
    } catch (e) {
      console.error("Failed to log error to localStorage", e);
    }
  }

  static getStoredErrors(): ErrorLogEntry[] {
    try {
      const stored = getPlatformStorageItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error("Failed to retrieve stored errors", e);
      return [];
    }
  }

  static clearStoredErrors() {
    removePlatformStorageItem(this.STORAGE_KEY);
  }

  static setupGlobalErrorHandler() {
    // Handle unhandled errors
    window.addEventListener("error", (event) => {
      this.logError(event.error || event.message, "global-error");
    });

    // Handle unhandled promise rejections
    window.addEventListener("unhandledrejection", (event) => {
      this.logError(
        event.reason instanceof Error ? event.reason : String(event.reason),
        "unhandled-rejection"
      );
    });
  }
}

export default ErrorLogger;
